import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCity } from '../contexts/CityContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';
import { BottomNav } from '../components/BottomNav';

// LE-020 (S4-c): listagem de eventos da cidade — GET /api/events?city_id=&upcoming=
// Padrão: próximos (upcoming=true); toggle mostra passados.

const LIMIT = 20;

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00'); // date-only ISO
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', weekday: 'short' });
}

function formatTime(t) {
  if (!t) return null;
  return t.slice(0, 5); // HH:MM
}

function formatLocation(address) {
  if (!address) return null;
  return address.length > 50 ? address.slice(0, 47) + '…' : address;
}

function getMonthDay(iso) {
  if (!iso) return {};
  const d = new Date(iso + 'T00:00:00');
  return {
    day: d.getDate(),
    month: d.toLocaleDateString('pt-BR', { month: 'short' }),
  };
}

export function CityEventsPage() {
  const navigate = useNavigate();
  const { selectedCity } = useCity();
  const { user } = useAuth();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upcoming, setUpcoming] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchEvents = useCallback(async (append = false) => {
    if (!selectedCity) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/events', {
        params: {
          city_id: selectedCity.id,
          upcoming: upcoming ? 'true' : 'false',
          page: append ? page : 1,
          limit: LIMIT,
        },
      });
      const list = data.data || [];
      setEvents(append ? (prev) => [...prev, ...list] : list);
      setTotal(data.pagination?.total || 0);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  }, [selectedCity, upcoming, page]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const toggleUpcoming = (val) => {
    if (val === upcoming) return;
    setUpcoming(val);
    setPage(1);
    setEvents([]);
  };

  const loadMore = () => {
    setPage((p) => p + 1);
  };

  // Efeito separado para loadMore: quando page muda, append=true
  useEffect(() => {
    if (page > 1) fetchEvents(true);
  }, [page]); // eslint-disable-line

  if (!selectedCity) return <Loading message="Carregando cidade..." />;

  return (
    <div className="nc-page nc-page--nav">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, var(--nc-boost) 0%, #5b21b6 100%)',
        color: 'var(--nc-on-primary)', padding: '18px 16px 22px', borderRadius: '0 0 22px 22px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button onClick={() => navigate('/mapa')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div style={{ flex: 1 }}>
            <h1 className="nc-page-title">Eventos em {selectedCity.name}</h1>
            <p style={{ margin: '2px 0 0', fontSize: 12, opacity: 0.85 }}>
              {upcoming ? 'Próximos eventos' : 'Eventos passados'}
            </p>
          </div>
          {user && (
            <button
              onClick={() => navigate('/eventos/novo')}
              style={{
                background: 'rgba(255,255,255,0.2)', color: 'var(--nc-on-primary)', border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
              Criar
            </button>
          )}
        </div>

        {/* Toggle upcoming/past */}
        <div style={{ display: 'flex', gap: 6 }}>
          {[{ val: true, label: 'Próximos' }, { val: false, label: 'Passados' }].map(({ val, label }) => (
            <button
              key={val}
              onClick={() => toggleUpcoming(val)}
              style={{
                padding: '7px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
                background: upcoming === val ? '#fff' : 'rgba(255,255,255,0.15)',
                color: upcoming === val ? '#5b21b6' : '#fff',
                transition: 'all 0.15s ease',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Event list */}
      <div className="nc-container" style={{ paddingTop: 14, paddingBottom: 14 }}>
        {error && (
          <div role="alert" style={{
            padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)',
            color: 'var(--nc-danger-text)', fontSize: 13, marginBottom: 12,
          }}>
            {error}
            <button className="nc-btn-primary" style={{ marginTop: 10, width: '100%' }} onClick={() => fetchEvents()}>
              Tentar novamente
            </button>
          </div>
        )}

        {loading && events.length === 0 ? <Loading message="Carregando eventos..." /> : events.length === 0 ? (
          <EmptyState
            icon="🎉"
            title={upcoming ? 'Nenhum evento próximo' : 'Nenhum evento passado'}
            description={upcoming ? 'Ainda não há eventos agendados para esta cidade. Seja o primeiro a criar um!' : 'Os eventos realizados aparecerão aqui.'}
            action={user ? (
              <button className="nc-btn-primary" onClick={() => navigate('/eventos/novo')}>
                Criar evento
              </button>
            ) : undefined}
          />
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {events.map((ev) => {
                const { day, month } = getMonthDay(ev.date);
                return (
                  <div
                    key={ev.id}
                    onClick={() => navigate(`/evento/${ev.id}`)}
                    style={{
                      background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)',
                      padding: 14, display: 'flex', gap: 14, cursor: 'pointer',
                      transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--nc-shadow-lg)'; e.currentTarget.style.borderColor = '#a78bfa'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = 'var(--nc-border)'; }}
                  >
                    {/* Date badge */}
                    <div style={{
                      width: 52, height: 52, borderRadius: 12, flexShrink: 0,
                      background: 'linear-gradient(135deg,var(--nc-boost),#5b21b6)',
                      color: 'var(--nc-on-primary)', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{day}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase' }}>{month}</span>
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 3 }}>
                        {ev.name}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 11, color: 'var(--nc-text-secondary)' }}>
                        {formatTime(ev.time_start) && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            🕐 {formatTime(ev.time_start)}{formatTime(ev.time_end) ? ` — ${formatTime(ev.time_end)}` : ''}
                          </span>
                        )}
                        {ev.business_name && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            🏪 {ev.business_name}
                          </span>
                        )}
                      </div>
                      {formatLocation(ev.address) && (
                        <div className="nc-caption" style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
                          📍 {formatLocation(ev.address)}
                        </div>
                      )}
                    </div>

                    <div style={{
                      display: 'flex', alignItems: 'center', flexShrink: 0, color: 'var(--nc-text-muted)',
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loading}
                style={{
                  width: '100%', padding: '12px 0', borderRadius: 10, border: '1px solid var(--nc-border)',
                  background: 'var(--nc-surface)', color: 'var(--nc-boost)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  marginTop: 16,
                }}
              >
                {loading ? 'Carregando...' : 'Carregar mais'}
              </button>
            )}

            {total > 0 && (
              <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 12, color: 'var(--nc-text-muted)' }}>
                {total} evento{total !== 1 ? 's' : ''}
              </div>
            )}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
